import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarController,
  LineController,
  BarElement,
  PointElement,
  LineElement,
  Tooltip,
  Legend,
} from 'chart.js';
import { Bar } from 'react-chartjs-2';
import './css/MonitoramentoPublico.css';
import { API_BASE_URL } from '../config/ApiConfig';
import { formatCurrencyView } from './utils/currencyUtils';
import PageCard from './components/PageCard';
import LoadingModal from './components/modals/LoadingModal';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarController,
  LineController,
  BarElement,
  PointElement,
  LineElement,
  Tooltip,
  Legend
);

const CORES = {
  receitas: 'rgba(25, 135, 84, .75)',
  receitasBorda: '#198754',
  rendimentos: 'rgba(13, 148, 136, .75)',
  rendimentosBorda: '#0d9488',
  despesas: 'rgba(220, 53, 69, .7)',
  despesasBorda: '#dc3545',
  saldoAcumulado: '#0d6efd',
};

const opcoesCompactas = {
  locale: 'pt-BR',
  notation: 'compact',
  compactDisplay: 'short',
};

function MonitoramentoInterno() {
  const navigate = useNavigate();
  const token = localStorage.getItem('token');
  const cnpj = localStorage.getItem('cnpj');
  const anoBase = parseInt(localStorage.getItem('anoBase')) || new Date().getFullYear();

  const [resumo, setResumo] = useState(null);
  const [carregando, setCarregando] = useState(true);
  const [mensagemErro, setMensagemErro] = useState('');

  useEffect(() => {
    if (!token || !cnpj) {
      navigate('/');
      return;
    }
  }, [token, cnpj, navigate]);

  // Carrega o resumo da entidade selecionada para o anoBase atual
  useEffect(() => {
    let ativo = true;
    const headers = { Authorization: `Bearer ${token}` };
    (async () => {
      if (!cnpj) return;
      setCarregando(true);
      setMensagemErro('');
      try {
        const { data } = await axios.get(`${API_BASE_URL.MONITORAMENTO}/resumo`, {
          params: { cnpjAssociacao: cnpj, ano: anoBase },
          headers,
        });
        if (ativo) setResumo(data);
      } catch (e) {
        if (ativo) {
          const msg = e?.response?.data?.message;
          setMensagemErro(msg || 'Não foi possível carregar os dados. Tente novamente mais tarde.');
          setResumo(null);
        }
      } finally {
        if (ativo) setCarregando(false);
      }
    })();
    return () => { ativo = false; };
  }, [cnpj, token, anoBase]);

  const dadosGrafico = useMemo(() => {
    if (!resumo) return null;
    const agora = new Date();
    const limite = Number(resumo.ano) === agora.getFullYear()
      ? agora.getMonth() + 1
      : resumo.meses.length;
    const mesesVisiveis = resumo.meses.slice(0, limite);
    const labels = mesesVisiveis.map(m => m.mes.substring(0, 3));
    return {
      labels,
      datasets: [
        {
          type: 'line',
          label: 'Saldo acumulado',
          data: mesesVisiveis.map(m => Number(m.saldoAcumulado)),
          borderColor: CORES.saldoAcumulado,
          backgroundColor: CORES.saldoAcumulado,
          borderWidth: 2,
          pointRadius: 3,
          tension: .35,
          order: 0,
        },
        {
          type: 'bar',
          label: 'Receitas',
          data: mesesVisiveis.map(m => Number(m.receitas)),
          backgroundColor: CORES.receitas,
          borderColor: CORES.receitasBorda,
          borderWidth: 1,
          borderRadius: 4,
          order: 1,
        },
        {
          type: 'bar',
          label: 'Rendimentos',
          data: mesesVisiveis.map(m => Number(m.rendimentos)),
          backgroundColor: CORES.rendimentos,
          borderColor: CORES.rendimentosBorda,
          borderWidth: 1,
          borderRadius: 4,
          order: 2,
        },
        {
          type: 'bar',
          label: 'Despesas',
          data: mesesVisiveis.map(m => Number(m.despesas)),
          backgroundColor: CORES.despesas,
          borderColor: CORES.despesasBorda,
          borderWidth: 1,
          borderRadius: 4,
          order: 3,
        },
      ],
    };
  }, [resumo]);

  const opcoesGrafico = useMemo(() => ({
    responsive: true,
    maintainAspectRatio: false,
    interaction: { mode: 'index', intersect: false },
    plugins: {
      legend: { position: 'bottom', labels: { usePointStyle: true, boxWidth: 8 } },
      tooltip: {
        callbacks: {
          label: contexto =>
            ` ${contexto.dataset.label}: ${formatCurrencyView(contexto.parsed.y)}`,
        },
      },
    },
    scales: {
      y: {
        ticks: {
          callback: valor => valor.toLocaleString('pt-BR', opcoesCompactas),
        },
      },
    },
  }), []);

  if (carregando) {
    return <LoadingModal loading={true} />;
  }

  return (
    <PageCard title="Monitoramento da Entidade" icon="bi-clipboard-data">
      <div className="container-fluid px-0">
        {mensagemErro && (
          <div className="alert alert-warning d-flex align-items-center gap-2 shadow-sm border-0 rounded-3" role="alert">
            <i className="bi bi-info-circle-fill fs-5"></i>
            <div>{mensagemErro}</div>
          </div>
        )}

        {resumo && (
          <>
            <h5 className="fw-bold mb-3">
              {resumo.nomeAssociacao}{' '}
              <span className="badge bg-success-subtle text-success-emphasis ms-1">
                Exercício {resumo.ano}
              </span>
            </h5>

            <div className="row g-3 mb-4">
              <TotalCard icone="bi-arrow-down-circle" cor="receitas" rotulo="Receitas" valor={resumo.totalReceitas} />
              <TotalCard icone="bi-graph-up-arrow" cor="rendimentos" rotulo="Rendimentos" valor={resumo.totalRendimentos} />
              <TotalCard icone="bi-arrow-up-circle" cor="despesas" rotulo="Despesas" valor={resumo.totalDespesas} />
              <TotalCard icone="bi-cash-stack" cor="saldo" rotulo="Saldo acumulado" valor={resumo.saldoAcumuladoFinal} />
            </div>
            <div className="card transp-section mb-4">
              <div className="card-body">
                <div className="transp-section-title">
                  <i className="bi bi-bar-chart-line"></i>
                  Movimento financeiro mensal
                </div>
                <div style={{ height: 300 }}>
                  {dadosGrafico && <Bar data={dadosGrafico} options={opcoesGrafico} />}
                </div>
              </div>
            </div>

            <div className="row g-4 mb-4">
              <div className="col-lg-7">
                <div className="card transp-section h-100">
                  <div className="card-body">
                    <div className="transp-section-title">
                      <i className="bi bi-calendar3"></i>
                      Detalhamento por mês
                    </div>
                    <div className="table-responsive-sm">
                      <table className="table table-hover table-sm transp-table mb-0">
                        <thead>
                          <tr>
                            <th>Mês</th>
                            <th className="text-end">Rendimentos</th>
                            <th className="text-end">Receitas</th>
                            <th className="text-end">Despesas</th>
                            <th className="text-end">Saldo</th>
                            <th className="text-end">Saldo acumulado</th>
                          </tr>
                        </thead>
                        <tbody>
                          {resumo.meses.filter(linhaTemMovimento).map(m => (
                            <tr key={m.mes}>
                              <td>{m.mes}</td>
                              <td className="text-end text-num">{formatCurrencyView(m.rendimentos)}</td>
                              <td className="text-end text-num">{formatCurrencyView(m.receitas)}</td>
                              <td className="text-end text-num">{formatCurrencyView(m.despesas)}</td>
                              <td className={`text-end text-num ${Number(m.saldo) < 0 ? 'text-danger' : ''}`}>
                                {formatCurrencyView(m.saldo)}
                              </td>
                              <td className={`text-end text-num fw-semibold ${Number(m.saldoAcumulado) < 0 ? 'text-danger' : ''}`}>
                                {formatCurrencyView(m.saldoAcumulado)}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                        <tfoot>
                          <tr className="table-light fw-bold">
                            <td>Total</td>
                            <td className="text-end text-num">{formatCurrencyView(resumo.totalRendimentos)}</td>
                            <td className="text-end text-num">{formatCurrencyView(resumo.totalReceitas)}</td>
                            <td className="text-end text-num">{formatCurrencyView(resumo.totalDespesas)}</td>
                            <td colSpan="2" className="text-end text-num">
                              {formatCurrencyView(resumo.saldoAcumuladoFinal)}
                            </td>
                          </tr>
                        </tfoot>
                      </table>
                    </div>
                    {resumo.meses.every(m => !linhaTemMovimento(m)) && (
                      <p className="text-muted small mt-2 mb-0">Nenhum movimento registrado neste exercício.</p>
                    )}
                  </div>
                </div>
              </div>

              <div className="col-lg-5 d-flex flex-column gap-4">
                <RankingCard
                  titulo="Despesas por categoria"
                  icone="bi-tags"
                  itens={resumo.despesasPorCategoria}
                  vazio="Nenhuma despesa paga neste exercício."
                />
                <RankingCard
                  titulo="Receitas por programa"
                  icone="bi-diagram-3"
                  itens={resumo.receitasPorPrograma}
                  vazio="Nenhuma receita registrada neste exercício."
                />
              </div>
            </div>
          </>
        )}
      </div>
    </PageCard>
  );
}

function linhaTemMovimento(mes) {
  return ['rendimentos', 'receitas', 'despesas'].some(
    campo => Number(mes[campo]) !== 0
  );
}

const CORES_ICONE = {
  receitas: '#198754',
  rendimentos: '#0d9488',
  despesas: '#dc3545',
  saldo: '#0d6efd',
};

function TotalCard({ icone, cor, rotulo, valor }) {
  return (
    <div className="col-sm-6 col-lg-3">
      <div className={`card transp-card-total transp-card-${cor}`}>
        <div className="card-body py-3">
          <div className="d-flex justify-content-between align-items-center">
            <span className="transp-label">{rotulo}</span>
            <i className={`bi ${icone}`} style={{ color: CORES_ICONE[cor], fontSize: '1.2rem' }}></i>
          </div>
          <div className={`transp-value ${Number(valor) < 0 ? 'text-danger' : ''}`}>
            {formatCurrencyView(valor)}
          </div>
        </div>
      </div>
    </div>
  );
}

function RankingCard({ titulo, icone, itens, vazio }) {
  const maximo = itens && itens.length > 0
    ? Math.max(...itens.map(i => Number(i.valor)))
    : 0;

  return (
    <div className="card transp-section flex-grow-1">
      <div className="card-body">
        <div className="transp-section-title">
          <i className={`bi ${icone}`}></i>
          {titulo}
        </div>
        {!itens || itens.length === 0 ? (
          <p className="text-muted small mb-0">{vazio}</p>
        ) : (
          <ul className="list-unstyled mb-0">
            {itens.map(item => {
              const percentual = maximo > 0 ? (Number(item.valor) / maximo) * 100 : 0;
              return (
                <li key={item.descricao} className="mb-3 last-no-margin">
                  <div className="d-flex justify-content-between align-items-baseline gap-2 mb-1">
                    <span className="small fw-semibold text-truncate" title={item.descricao}>
                      {item.descricao}
                    </span>
                    <span className="small text-nowrap fw-semibold">{formatCurrencyView(item.valor)}</span>
                  </div>
                  <div className="progress" style={{ height: 6 }}>
                    <div
                      className="progress-bar bg-success"
                      role="progressbar"
                      style={{ width: `${percentual}%` }}
                      aria-valuenow={percentual}
                      aria-valuemin="0"
                      aria-valuemax="100"
                    ></div>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}

export default MonitoramentoInterno;